/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useState } from 'react';

import { useLocation } from 'js/lib/useRouter';

import { IconButton } from '@coursera/cds-core';

import type { ConsumerPageHeader_EnterpriseProgramFragment as EnterpriseProgramFragment } from 'bundles/composable-page-header-consumer/queries/__generated__/ConsumerMyPageHeaderQuery';
import {
  ORG_SCOPED_ROUTE_PREFIX,
  isOnOrgScopedRoute,
} from 'bundles/composable-page-header-enterprise/utils/orgScopedRouteUtils';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import EnterpriseSearchBar from 'bundles/search-common/components/searchbar/enterprise/SearchBar';
import { useMobileWebConversationalDiscoveryExperiment } from 'bundles/unified-common/contexts/MobileWebConversationalDiscoveryExperimentContext';

import _t from 'i18n!nls/composable-page-header-enterprise';

const styles = {
  mobileSearchPage: css`
    position: fixed;
    top: -120vh;
    left: 0;
    background-color: transparent;
    height: 100vh;
    padding-top: 65px;
    transition: transform 0.5s ease-in-out;
    overflow: visible;
    z-index: 1;
  `,
  openMobileSearch: css`
    transform: translateY(120vh);
  `,
  mobileSearchBodyWrapper: css`
    background-color: var(--cds-color-neutral-background-primary);
    height: 100vh;
    width: 100vw;
    overflow: scroll;
  `,
  searchIcon: css`
    color: var(--cds-color-neutral-primary-weak);
  `,
};

type Props = {
  thirdPartyOrganizationSlug?: string;
  enrolledEnterprisePrograms?: Partial<EnterpriseProgramFragment>[] | null;
  chosenProgramSlug?: string;
  chosenProgramId?: string;
};

export function EnterpriseOrgScopedMobileSearchBarGridItem({
  thirdPartyOrganizationSlug,
  enrolledEnterprisePrograms,
  chosenProgramSlug,
  chosenProgramId,
}: Props) {
  const [searchIsOpen, setSearchIsOpen] = useState(false);
  const { pathname } = useLocation();
  // Matches the icon row's tight Figma spacing in the mobile-web conversational-discovery
  // treatment (MERCH-3449) by dropping this button's own hit-box padding. Gated since this
  // component is shared beyond XDP.
  const { mobileWebConversationalDiscoveryEnabled } = useMobileWebConversationalDiscoveryExperiment();

  useEffect(() => {
    const bodyStyle = document.body.style;
    bodyStyle.overflow = searchIsOpen ? 'hidden' : 'visible';
    return () => {
      bodyStyle.overflow = 'visible';
    };
  }, [searchIsOpen]);

  if (!thirdPartyOrganizationSlug) {
    return null;
  }

  const onOrgScopedRoute = isOnOrgScopedRoute(pathname);

  const orgPrograms = enrolledEnterprisePrograms?.filter(
    (program) => program?.naptimeThirdPartyOrganization?.slug === thirdPartyOrganizationSlug
  );

  const enrolledProgramIds = orgPrograms?.flatMap((program) => (program?.id ? [program.id] : [])) ?? [];
  // Fall back to the current page's program so logged-in users without enrollments
  // in this org (e.g. admins and CSMs viewing a program) still get a scoped search bar.
  const programIds: Array<string> =
    enrolledProgramIds.length === 0 && chosenProgramId ? [chosenProgramId] : enrolledProgramIds;
  const primaryProgramSlug = chosenProgramSlug ?? orgPrograms?.[0]?.slug;

  if (!onOrgScopedRoute && (!primaryProgramSlug || !programIds.length)) {
    return null;
  }

  const orgName = orgPrograms?.[0]?.naptimeThirdPartyOrganization?.name;
  const searchPath = onOrgScopedRoute
    ? `${ORG_SCOPED_ROUTE_PREFIX}${thirdPartyOrganizationSlug}/search`
    : `/programs/${primaryProgramSlug}/browse`;

  const disableScrolling = (disabled: boolean) => {
    const bodyStyle = document?.body?.style;
    if (bodyStyle) {
      bodyStyle.overflow = disabled ? 'hidden' : 'visible';
    }
  };

  const showSearch = () => {
    setSearchIsOpen(true);
    disableScrolling(true);
  };

  const hideSearch = () => {
    setSearchIsOpen(false);
    disableScrolling(false);
  };

  return (
    <React.Fragment>
      {!searchIsOpen && (
        <IconButton
          intent="search"
          variant="ghost"
          onClick={showSearch}
          css={styles.searchIcon}
          edgeAlign={mobileWebConversationalDiscoveryEnabled ? 'noPadding' : undefined}
        />
      )}
      <div css={[styles.mobileSearchPage, searchIsOpen && styles.openMobileSearch]}>
        <div css={styles.mobileSearchBodyWrapper}>
          {searchIsOpen && (
            <TrackedDiv trackingName="mobile_enterprise_search_contents" trackClicks={false} withVisibilityTracking>
              <EnterpriseSearchBar
                enterpriseSearchBarConfig={{
                  programIds,
                  pathname: searchPath,
                  searchResultsBasePath: searchPath,
                  placeholder: orgName
                    ? _t('Search #{orgName} learning on Coursera', { orgName })
                    : _t('Search learning on Coursera'),
                  ...(onOrgScopedRoute
                    ? { enterpriseOrgSlug: thirdPartyOrganizationSlug }
                    : { enterpriseProgramSlug: primaryProgramSlug }),
                }}
                searchIsOpen={searchIsOpen}
                hideMobileSearchPage={hideSearch}
              />
            </TrackedDiv>
          )}
        </div>
      </div>
    </React.Fragment>
  );
}

export default EnterpriseOrgScopedMobileSearchBarGridItem;
